import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CheckCheckIcon, EyeIcon, EyeOffIcon} from '../components/icons';
import AuthShell from '../components/auth/AuthShell';
import SmartCaptcha, {isCaptchaEnabled} from '../components/auth/SmartCaptcha';
import {appName} from '../components/landing/content';
import {LEGAL_DOCUMENTS, legalPath} from '../legal';
import {AuthApiError, register} from '../service/RegistrationService';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/**
 * Регистрация (`docs/features/2026-08-20_auth-registration-recovery`, FR-24/FR-27).
 *
 * Успех выглядит одинаково и для свободного, и для занятого адреса: экран говорит
 * «проверьте почту» и ничего не сообщает о существовании аккаунта.
 */
const RegisterPage: React.FC = () => {
    useDocumentTitle();
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    // Токен капчи одноразовый: после отказа сервера виджет пересоздаём, иначе повторная
    // отправка уйдёт с уже использованным токеном и снова упадёт.
    const [captchaEpoch, setCaptchaEpoch] = useState(0);
    const alertRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    // Рассылки — только явный opt-in (QA-14, 2026-09-15_exploratory-qa-fixes §2.8):
    // чекбокс по умолчанию снят, пользователь включает его сам; отозвать можно в профиле.
    const [marketingConsent, setMarketingConsent] = useState(false);
    // Обязательное согласие с документами сервиса: без него регистрации нет
    // (2026-08-26_legal-documents, C-1). Намеренно отдельное от рассылок —
    // смешивать обязательное согласие с маркетинговым нельзя (C-3).
    const [legalConsent, setLegalConsent] = useState(false);
    // Ошибка именно про согласие живёт рядом с чекбоксом: на неё указывает
    // `aria-describedby`, а общий баннер наверху для этого слишком далеко.
    const [legalConsentError, setLegalConsentError] = useState('');

    const captchaRequired = isCaptchaEnabled();

    // Ошибка появляется над формой, вне поля зрения того, кто только что нажал кнопку внизу:
    // переводим на неё фокус — скринридер озвучит, экран доскроллит.
    useEffect(() => {
        if (error) alertRef.current?.focus();
    }, [error]);
    const handleCaptchaToken = useCallback((token: string) => setCaptchaToken(token), []);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmation) {
            setError('Пароли не совпадают.');
            return;
        }
        if (!legalConsent) {
            setLegalConsentError('Чтобы создать аккаунт, примите документы сервиса.');
            return;
        }
        setLegalConsentError('');
        setLoading(true);
        setError('');
        try {
            await register({
                email, password, displayName, marketingConsent, captchaToken,
                legalConsentAccepted: legalConsent,
            });
            setSubmitted(true);
        } catch (err) {
            setError(err instanceof AuthApiError ? err.description : 'Не удалось зарегистрироваться. Попробуйте позже.');
            setCaptchaToken('');
            setCaptchaEpoch(n => n + 1);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            heading={<>Заведите свой шкаф<br/>в <span className="text-brand-600 dark:text-brand-400">{appName()}</span></>}
            lead="Регистрация занимает минуту: подтвердите адрес почты и добавьте первую вещь — остальное подскажет AI."
        >
            <div data-testid="register-page">
                {submitted ? (
                    <div className="mt-6 text-center" data-testid="register-success">
                        <span className="mx-auto w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center">
                            <CheckCheckIcon className="w-6 h-6 text-brand-600 dark:text-brand-400"/>
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Проверьте почту</h2>
                        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">
                            Если этот адрес свободен, мы отправили на него письмо со ссылкой подтверждения.
                            Ссылка действует сутки — до подтверждения вход в аккаунт закрыт.
                        </p>
                        <a href="/login" className="btn-secondary h-11 mt-6 w-full">Вернуться ко входу</a>
                    </div>
                ) : (
                    <>
                        <h2 className="mt-6 text-2xl font-bold text-center text-ink-900 dark:text-ink-50">
                            Создать аккаунт
                        </h2>
                        <p className="mt-2 text-sm text-center text-ink-500 dark:text-ink-400">
                            Логином будет ваш адрес почты.
                        </p>

                        {error && (
                            <div
                                ref={alertRef}
                                role="alert"
                                tabIndex={-1}
                                className="mt-6 p-3.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                            >
                                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        )}

                        <form onSubmit={onSubmit} className="mt-6 space-y-5">
                            <div>
                                <label htmlFor="displayName"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Как к вам обращаться
                                </label>
                                <input
                                    id="displayName"
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    className="input-field h-12"
                                    placeholder="Например, Иван"
                                    autoComplete="given-name"
                                    maxLength={100}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label htmlFor="email"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Электронная почта
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-field h-12"
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="password"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Пароль
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="input-field h-12 pr-12"
                                        placeholder="Не короче 10 символов"
                                        autoComplete="new-password"
                                        minLength={10}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 px-3 flex items-center text-ink-400 hover:text-ink-600 dark:hover:text-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                        aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOffIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}
                                    </button>
                                </div>
                                <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
                                    Минимум 10 символов, хотя бы одна буква, без адреса почты внутри.
                                </p>
                            </div>

                            <div>
                                <label htmlFor="confirmation"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Повторите пароль
                                </label>
                                <input
                                    id="confirmation"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmation}
                                    onChange={(e) => setConfirmation(e.target.value)}
                                    className="input-field h-12"
                                    autoComplete="new-password"
                                    required
                                />
                            </div>

                            {/* Тач-зона: min-h-11 и вертикальные отступы делают весь ряд
                                (чекбокс + подпись) кликабельным на ≥ 44px. */}
                            <div className="flex items-start gap-3 min-h-11 py-1">
                                <input
                                    id="legalConsent"
                                    type="checkbox"
                                    required
                                    checked={legalConsent}
                                    onChange={(e) => {
                                        setLegalConsent(e.target.checked);
                                        if (e.target.checked) setLegalConsentError('');
                                    }}
                                    aria-describedby={legalConsentError ? 'legalConsentError' : 'legalConsentHint'}
                                    aria-invalid={legalConsentError ? true : undefined}
                                    className="mt-0.5 h-4 w-4 rounded border-ink-300 dark:border-white/20 text-brand-600 focus:ring-brand-500"
                                />
                                <div className="flex-1">
                                    <label
                                        htmlFor="legalConsent"
                                        className="block text-sm leading-5 text-ink-600 dark:text-ink-300"
                                    >
                                        Я принимаю{' '}
                                        {LEGAL_DOCUMENTS.map((document, index) => (
                                            <React.Fragment key={document.slug}>
                                                {index > 0 && (index === LEGAL_DOCUMENTS.length - 1 ? ' и ' : ', ')}
                                                {/* Новая вкладка намеренно: заполненная форма не должна
                                                    теряться, когда человек уходит читать документ (C-2). */}
                                                <a
                                                    href={legalPath(document)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-brand-600 dark:text-brand-400 hover:underline"
                                                >
                                                    {document.title.toLowerCase()}
                                                </a>
                                            </React.Fragment>
                                        ))}
                                        .
                                    </label>
                                    {legalConsentError ? (
                                        <p id="legalConsentError" className="mt-1 text-xs text-red-700 dark:text-red-300">
                                            {legalConsentError}
                                        </p>
                                    ) : (
                                        <p id="legalConsentHint" className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                                            Без согласия создать аккаунт нельзя.
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-start gap-3 min-h-11 py-1">
                                <input
                                    id="marketingConsent"
                                    type="checkbox"
                                    checked={marketingConsent}
                                    onChange={(e) => setMarketingConsent(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-ink-300 dark:border-white/20 text-brand-600 focus:ring-brand-500"
                                />
                                <label htmlFor="marketingConsent" className="text-sm leading-5 text-ink-600 dark:text-ink-300">
                                    Присылать новости и подборки на почту.
                                    Отключить можно в любой момент в настройках профиля.
                                </label>
                            </div>

                            <SmartCaptcha key={captchaEpoch} onToken={handleCaptchaToken}/>

                            <button
                                type="submit"
                                disabled={loading || !legalConsent || (captchaRequired && !captchaToken)}
                                className="btn-accent w-full h-12 text-[15px]"
                            >
                                {loading ? 'Отправляем...' : 'Создать аккаунт'}
                            </button>

                            <a href="/login" className="btn-secondary w-full h-12 text-[15px]">
                                У меня уже есть аккаунт
                            </a>
                        </form>
                    </>
                )}
            </div>
        </AuthShell>
    );
};

export default RegisterPage;
